import React, { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { ApiService } from '../../services/api';
import { 
  CircularProgress, 
  Box, 
  Typography, 
  TextField, 
  Button, 
  Alert, 
  Paper, 
  Container, 
  Grid 
} from '@mui/material';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { isAuthenticated, isLoading, user, refreshUser, logout } = useAuth();
  const [tfaLoading, setTfaLoading] = useState(true);
  const [tfaError, setTfaError] = useState<string | null>(null);
  const [qrCode, setQrCode] = useState<string | null>(null);
  const [tfaSecret, setTfaSecret] = useState<string | null>(null);
  const [verificationCode, setVerificationCode] = useState('');
  const [enabling, setEnabling] = useState(false);

  // Load 2FA setup details if user has 2FA disabled
  useEffect(() => {
    if (isAuthenticated && user && !user.tfaEnabled) {
      const load2FASetup = async () => {
        setTfaLoading(true);
        setTfaError(null);
        try {
          const res = await ApiService.setup2FA();
          if (res.success) {
            setQrCode(res.qrCode);
            setTfaSecret(res.manualEntryKey);
          } else {
            setTfaError(res.message || 'Failed to initiate 2FA setup.');
          }
        } catch (err) {
          setTfaError(err instanceof Error ? err.message : 'An error occurred during 2FA setup.');
        } finally {
          setTfaLoading(false);
        }
      };
      load2FASetup();
    }
  }, [isAuthenticated, user?.tfaEnabled]);

  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // FORCE 2FA: If authenticated but 2FA is not enabled, block navigation & render setup flow
  if (user && !user.tfaEnabled && false) {
    const handleEnable = async (e: React.FormEvent) => {
      e.preventDefault();
      if (!verificationCode || verificationCode.trim().length !== 6) {
        setTfaError('Please enter a valid 6-digit verification code.');
        return;
      }
      setEnabling(true);
      setTfaError(null);
      try {
        const res = await ApiService.enable2FA(verificationCode.trim());
        if (res.success) {
          await refreshUser(); // Fetch updated profile containing tfaEnabled = true
        } else {
          setTfaError(res.message || 'Incorrect verification code. Please try again.');
        }
      } catch (err) {
        setTfaError(err instanceof Error ? err.message : 'Failed to enable 2FA.');
      } finally {
        setEnabling(false);
      }
    };

    return (
      <Box
        sx={{
          background: 'linear-gradient(135deg, #0f172a 0%, #1e1b4b 100%)',
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          py: 6,
          px: 2,
        }}
      >
        <Container maxWidth="sm">
          <Paper
            elevation={24}
            sx={{
              p: 4.5,
              borderRadius: 3,
              backgroundColor: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(10px)',
              boxShadow: '0 20px 40px rgba(0, 0, 0, 0.4)',
              textAlign: 'center',
            }}
          >
            <Typography variant="h4" sx={{ fontWeight: 800, mb: 1.5, color: '#0f172a', letterSpacing: '-0.5px' }}>
              Setup Two-Factor Authentication
            </Typography>
            <Typography variant="body2" sx={{ color: '#475569', mb: 3.5, maxWidth: 420, mx: 'auto', lineHeight: 1.6 }}>
              To secure your account and protect sensitive domain & outreach data, we require two-factor authentication (2FA) for all users.
            </Typography>

            {tfaError && (
              <Alert severity="error" sx={{ mb: 3, borderRadius: 2, textAlign: 'left' }} onClose={() => setTfaError(null)}>
                {tfaError}
              </Alert>
            )}

            {tfaLoading ? (
              <Box sx={{ py: 6 }}>
                <CircularProgress />
                <Typography variant="body2" sx={{ mt: 2, color: '#64748b' }}>
                  Generating secure 2FA token keys...
                </Typography>
              </Box>
            ) : (
              <Box component="form" onSubmit={handleEnable}>
                <Grid container spacing={3} sx={{ mb: 3, justifyContent: 'center' }}>
                  {qrCode && (
                    <Grid item xs={12}>
                      <Box
                        sx={{
                          display: 'inline-block',
                          p: 1.5,
                          backgroundColor: 'white',
                          borderRadius: 2,
                          border: '1px solid #e2e8f0',
                          boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                          mb: 1.5,
                        }}
                      >
                        <img src={qrCode || undefined} alt="Scan QR Code" style={{ display: 'block', maxWidth: '170px', height: 'auto' }} />
                      </Box>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e293b', mb: 0.5 }}>
                        Scan this QR Code
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#64748b', mb: 1.5 }}>
                        Use Google Authenticator, Authy, or any standard TOTP app.
                      </Typography>
                      {tfaSecret && (
                        <Box sx={{ backgroundColor: '#f1f5f9', p: 1, px: 2, borderRadius: 1.5, display: 'inline-flex', alignItems: 'center', gap: 1, border: '1px dashed #cbd5e1' }}>
                          <Typography variant="caption" sx={{ fontFamily: 'monospace', fontWeight: 600, color: '#334155' }}>
                            Key: {tfaSecret}
                          </Typography>
                        </Box>
                      )}
                    </Grid>
                  )}

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Enter 6-Digit Code"
                      value={verificationCode}
                      onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      placeholder="000 000"
                      disabled={enabling}
                      required
                      inputProps={{ 
                        style: { textAlign: 'center', letterSpacing: '8px', fontSize: '1.25rem', fontWeight: 'bold', fontFamily: 'monospace' } 
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                        }
                      }}
                    />
                  </Grid>
                </Grid>

                <Button
                  fullWidth
                  variant="contained"
                  type="submit"
                  size="large"
                  disabled={enabling || verificationCode.trim().length !== 6}
                  sx={{
                    py: 1.5,
                    fontSize: '1rem',
                    fontWeight: 600,
                    textTransform: 'none',
                    borderRadius: 2,
                    backgroundColor: '#1e1b4b',
                    boxShadow: '0 4px 12px rgba(30, 27, 75, 0.25)',
                    '&:hover': {
                      backgroundColor: '#0f172a',
                      boxShadow: '0 6px 16px rgba(15, 23, 42, 0.35)',
                    }
                  }}
                >
                  {enabling ? 'Verifying...' : 'Enable Two-Factor Authentication'}
                </Button>
              </Box>
            )}

            <Button
              onClick={logout}
              sx={{
                mt: 3,
                textTransform: 'none',
                fontWeight: 500,
                color: '#64748b',
                '&:hover': { color: '#ef4444', backgroundColor: 'transparent' }
              }}
            >
              Sign Out & Use Another Account
            </Button>
          </Paper>
        </Container>
      </Box>
    );
  }

  return <>{children}</>;
};

export default ProtectedRoute;

